export type Language = 'fr' | 'en';

export interface Translations {
  common: {
    search: string;
    cart: string;
    favorites: string;
    login: string;
    logout: string;
    profile: string;
    settings: string;
    orders: string;
    viewAll: string;
    learnMore: string;
    contactUs: string;
    browseProducts: string;
    shopNow: string;
    addToCart: string;
    viewDetails: string;
    outOfStock: string;
    lowStock: string;
    new: string;
    featured: string;
    price: string;
    quantity: string;
    total: string;
    checkout: string;
    continueShopping: string;
    remove: string;
    empty: string;
    loading: string;
    error: string;
    success: string;
    cancel: string;
    save: string;
    edit: string;
    delete: string;
    confirm: string;
    back: string;
    next: string;
    previous: string;
    submit: string;
    reset: string;
    filter: string;
    sort: string;
    clear: string;
    apply: string;
    close: string;
    open: string;
    yes: string;
    no: string;
    ok: string;
  };
  navigation: {
    home: string;
    products: string;
    favorites: string;
    about: string;
    contact: string;
    categories: string;
    account: string;
  };
  home: {
    heroTitle: string;
    heroSubtitle: string;
    features: {
      fastDelivery: {
        title: string;
        description: string;
      };
      securePayment: {
        title: string;
        description: string;
      };
      support: {
        title: string;
        description: string;
      };
    };
    categories: {
      title: string;
      subtitle: string;
    };
    featuredProducts: {
      title: string;
    };
    newProducts: {
      title: string;
    };
    callToAction: {
      title: string;
      subtitle: string;
    };
  };
  products: {
    title: string;
    search: string;
    filter: string;
    sort: string;
    addToCart: string;
    viewDetails: string;
    allCategories: string;
    newest: string;
    priceAsc: string;
    priceDesc: string;
    popular: string;
    heroTitle: string;
    heroSubtitle: string;
    shopNow: string;
    freeShipping: string;
    securePayment: string;
    support: string;
    easyReturns: string;
    new: string;
    specifications: string;
    features: string;
    reviews: string;
    stock: string;
    inStock: string;
    lowStock: string;
    searchPlaceholder: string;
    category: string;
    sortBy: string;
    sortName: string;
    sortPriceLow: string;
    sortPriceHigh: string;
    sortRating: string;
    sortNewest: string;
    filters: string;
    priceRange: string;
    categories: string;
    stockStatus: string;
    resultsFound: string;
    noResults: string;
    tryDifferentSearch: string;
  };
  cart: {
    title: string;
    empty: string;
    continueShopping: string;
    checkout: string;
    total: string;
    remove: string;
    quantity: string;
    subtotal: string;
    shipping: string;
    tax: string;
    grandTotal: string;
    proceedToCheckout: string;
    updateCart: string;
    clearCart: string;
  };
  favorites: {
    title: string;
    empty: string;
    emptyDescription: string;
    discoverProducts: string;
    clearFavorites: string;
    clearFavoritesConfirm: string;
    favoritesCleared: string;
    inCart: string;
    addToCart: string;
    viewDetails: string;
    removeFromFavorites: string;
    productRemoved: string;
  };
  categories: {
    buildingMaterials: {
      name: string;
      description: string;
    };
    toolsEquipment: {
      name: string;
      description: string;
    };
    plumbingElectrical: {
      name: string;
      description: string;
    };
    finishingMaterials: {
      name: string;
      description: string;
    };
    hardwareFasteners: {
      name: string;
      description: string;
    };
  };
  footer: {
    companyDescription: string;
    quickLinks: string;
    categories: string;
    contactUs: string;
    copyright: string;
    privacyPolicy: string;
    termsOfService: string;
  };
  profile: {
    title: string;
    edit: string;
    personalInfo: string;
    fullName: string;
    phone: string;
    email: string;
    company: string;
    address: string;
    registrationDate: string;
    recentOrders: string;
    viewAllOrders: string;
    stats: {
      orders: string;
      favorites: string;
      reviews: string;
    };
    verifiedAccount: string;
    memberSince: string;
    notProvided: string;
    editProfile: string;
    firstName: string;
    lastName: string;
    save: string;
    cancel: string;
    profileUpdated: string;
    profileUpdateError: string;
  };
  settings: {
    title: string;
    notifications: string;
    security: string;
    preferences: string;
    accountManagement: string;
    emailNotifications: string;
    smsNotifications: string;
    pushNotifications: string;
    orderUpdates: string;
    promotions: string;
    newsletter: string;
    twoFactorAuth: string;
    loginAlerts: string;
    darkMode: string;
    language: string;
    currency: string;
    personalData: string;
    paymentMethods: string;
    deleteAccount: string;
    manage: string;
    delete: string;
    saveSettings: string;
    settingsSaved: string;
    settingsError: string;
    themeUpdated: string;
    languageUpdated: string;
    settingUpdated: string;
    deleteAccountTitle: string;
    deleteAccountWarning: string;
    deleteAccountConfirm: string;
    deleteAccountError: string;
    darkModeDescription: string;
    languageDescription: string;
    currencyDescription: string;
    emailNotificationsDescription: string;
    smsNotificationsDescription: string;
    pushNotificationsDescription: string;
    orderUpdatesDescription: string;
    promotionsDescription: string;
    newsletterDescription: string;
    twoFactorAuthDescription: string;
    loginAlertsDescription: string;
    personalDataDescription: string;
    paymentMethodsDescription: string;
    deleteAccountDescription: string;
  };
}

const translations: Record<Language, Translations> = {
  fr: {
    common: {
      search: 'Rechercher',
      cart: 'Panier',
      favorites: 'Favoris',
      login: 'Connexion',
      logout: 'Déconnexion',
      profile: 'Profil',
      settings: 'Paramètres',
      orders: 'Commandes',
      viewAll: 'Voir tout',
      learnMore: 'En savoir plus',
      contactUs: 'Contactez-nous',
      browseProducts: 'Parcourir les produits',
      shopNow: 'Acheter maintenant',
      addToCart: 'Ajouter au panier',
      viewDetails: 'Voir les détails',
      outOfStock: 'Rupture de stock',
      lowStock: 'Stock limité',
      new: 'Nouveau',
      featured: 'En vedette',
      price: 'Prix',
      quantity: 'Quantité',
      total: 'Total',
      checkout: 'Commander',
      continueShopping: 'Continuer les achats',
      remove: 'Supprimer',
      empty: 'Vide',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      cancel: 'Annuler',
      save: 'Enregistrer',
      edit: 'Modifier',
      delete: 'Supprimer',
      confirm: 'Confirmer',
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      submit: 'Soumettre',
      reset: 'Réinitialiser',
      filter: 'Filtrer',
      sort: 'Trier',
      clear: 'Effacer',
      apply: 'Appliquer',
      close: 'Fermer',
      open: 'Ouvrir',
      yes: 'Oui',
      no: 'Non',
      ok: 'OK',
    },
    navigation: {
      home: 'Accueil',
      products: 'Produits',
      favorites: 'Favoris',
      about: 'À propos',
      contact: 'Contact',
      categories: 'Catégories',
      account: 'Compte',
    },
    home: {
      heroTitle: 'Votre partenaire de confiance en',
      heroSubtitle: 'Outils, équipements et fournitures de qualité avec service professionnel et prix compétitifs.',
      features: {
        fastDelivery: {
          title: 'Livraison rapide',
          description: 'Livraison le même jour pour les commandes passées avant 14h',
        },
        securePayment: {
          title: 'Paiement sécurisé',
          description: 'Paiement 100% sécurisé avec chiffrement SSL',
        },
        support: {
          title: 'Support 24/7',
          description: 'Support client disponible en permanence',
        },
      },
      categories: {
        title: 'Acheter par catégorie',
        subtitle: 'Trouvez tout ce dont vous avez besoin en outils, équipements et fournitures',
      },
      featuredProducts: {
        title: 'Produits en vedette',
      },
      newProducts: {
        title: 'Nouveautés',
      },
      callToAction: {
        title: 'Prêt à commencer votre projet ?',
        subtitle: 'Obtenez des équipements et fournitures de qualité livrés rapidement. Contactez-nous pour les commandes en gros et les prix spéciaux.',
      },
    },
    products: {
      title: 'Produits',
      search: 'Rechercher des produits...',
      filter: 'Filtrer',
      sort: 'Trier',
      addToCart: 'Ajouter au panier',
      viewDetails: 'Voir les détails',
      allCategories: 'Toutes les catégories',
      newest: 'Plus récents',
      priceAsc: 'Prix : Croissant',
      priceDesc: 'Prix : Décroissant',
      popular: 'Plus populaires',
      heroTitle: 'Découvrez nos produits',
      heroSubtitle: 'Trouvez les meilleurs outils, équipements et fournitures',
      shopNow: 'Acheter maintenant',
      freeShipping: 'Livraison gratuite',
      securePayment: 'Paiement sécurisé',
      support: 'Support 24/7',
      easyReturns: 'Retours faciles',
      new: 'Nouveau',
      specifications: 'Spécifications',
      features: 'Caractéristiques',
      reviews: 'Avis',
      stock: 'Stock',
      inStock: 'En stock',
      lowStock: 'Stock limité',
      searchPlaceholder: 'Rechercher un produit...',
      category: 'Catégorie',
      sortBy: 'Trier par',
      sortName: 'Nom',
      sortPriceLow: 'Prix : Croissant',
      sortPriceHigh: 'Prix : Décroissant',
      sortRating: 'Évaluation',
      sortNewest: 'Plus récent',
      filters: 'Filtres',
      priceRange: 'Plage de prix',
      categories: 'Catégories',
      stockStatus: 'État du stock',
      resultsFound: 'Résultats trouvés',
      noResults: 'Aucun résultat trouvé',
      tryDifferentSearch: 'Essayez une recherche différente',
    },
    cart: {
      title: 'Panier',
      empty: 'Votre panier est vide',
      continueShopping: 'Continuer les achats',
      checkout: 'Commander',
      total: 'Total',
      remove: 'Supprimer',
      quantity: 'Quantité',
      subtotal: 'Sous-total',
      shipping: 'Livraison',
      tax: 'Taxe',
      grandTotal: 'Total général',
      proceedToCheckout: 'Procéder au paiement',
      updateCart: 'Mettre à jour le panier',
      clearCart: 'Vider le panier',
    },
    favorites: {
      title: 'Favoris',
      empty: 'Votre liste de favoris est vide',
      emptyDescription: 'Explorez nos produits pour les ajouter à votre liste de favoris',
      discoverProducts: 'Découvrir des produits',
      clearFavorites: 'Vider la liste des favoris',
      clearFavoritesConfirm: 'Êtes-vous sûr de vouloir vider la liste des favoris ?',
      favoritesCleared: 'Liste des favoris vidée',
      inCart: 'Dans le panier',
      addToCart: 'Ajouter au panier',
      viewDetails: 'Voir les détails',
      removeFromFavorites: 'Supprimer des favoris',
      productRemoved: 'Produit supprimé des favoris',
    },
    categories: {
      buildingMaterials: {
        name: 'Matériaux',
        description: 'Catégorie désactivée',
      },
      toolsEquipment: {
        name: 'Outils et équipements',
        description: 'Outils professionnels et équipements',
      },
      plumbingElectrical: {
        name: 'Utilitaires & fournitures',
        description: 'Fournitures et consommables (plomberie/électricité)',
      },
      finishingMaterials: {
        name: 'Finitions',
        description: 'Catégorie désactivée',
      },
      hardwareFasteners: {
        name: 'Quincaillerie',
        description: 'Catégorie désactivée',
      },
    },
    footer: {
      companyDescription: 'Votre partenaire de confiance pour vos besoins en outils, équipements et fournitures. Qualité, service professionnel et prix compétitifs.',
      quickLinks: 'Liens rapides',
      categories: 'Catégories',
      contactUs: 'Contactez-nous',
      copyright: '© {year} TotalBat. Tous droits réservés.',
      privacyPolicy: 'Politique de confidentialité',
      termsOfService: 'Conditions d\'utilisation',
    },
    profile: {
      title: 'Profil',
      edit: 'Modifier',
      personalInfo: 'Informations personnelles',
      fullName: 'Nom complet',
      phone: 'Téléphone',
      email: 'Email',
      company: 'Entreprise',
      address: 'Adresse',
      registrationDate: 'Date d\'inscription',
      recentOrders: 'Commandes récentes',
      viewAllOrders: 'Voir toutes les commandes',
      stats: {
        orders: 'Commandes',
        favorites: 'Favoris',
        reviews: 'Avis',
      },
      verifiedAccount: 'Compte vérifié',
      memberSince: 'Membre depuis',
      notProvided: 'Non fourni',
      editProfile: 'Modifier le profil',
      firstName: 'Prénom',
      lastName: 'Nom',
      save: 'Enregistrer',
      cancel: 'Annuler',
      profileUpdated: 'Profil mis à jour',
      profileUpdateError: 'Erreur lors de la mise à jour du profil',
    },
    settings: {
      title: 'Paramètres',
      notifications: 'Notifications',
      security: 'Sécurité',
      preferences: 'Préférences',
      accountManagement: 'Gestion du compte',
      emailNotifications: 'Notifications par email',
      smsNotifications: 'Notifications par SMS',
      pushNotifications: 'Notifications push',
      orderUpdates: 'Mises à jour de commande',
      promotions: 'Promotions',
      newsletter: 'Newsletter',
      twoFactorAuth: 'Authentification à deux facteurs',
      loginAlerts: 'Alertes de connexion',
      darkMode: 'Mode sombre',
      language: 'Langue',
      currency: 'Devise',
      personalData: 'Données personnelles',
      paymentMethods: 'Méthodes de paiement',
      deleteAccount: 'Supprimer le compte',
      manage: 'Gérer',
      delete: 'Supprimer',
      saveSettings: 'Enregistrer les paramètres',
      settingsSaved: 'Paramètres enregistrés',
      settingsError: 'Erreur lors de la sauvegarde des paramètres',
      themeUpdated: 'Thème mis à jour',
      languageUpdated: 'Langue mise à jour',
      settingUpdated: 'Paramètre mis à jour',
      deleteAccountTitle: 'Supprimer le compte',
      deleteAccountWarning: 'Êtes-vous sûr de vouloir supprimer ce compte ?',
      deleteAccountConfirm: 'Confirmer la suppression',
      deleteAccountError: 'Erreur lors de la suppression du compte',
      darkModeDescription: 'Mode sombre',
      languageDescription: 'Langue',
      currencyDescription: 'Devise',
      emailNotificationsDescription: 'Notifications par email',
      smsNotificationsDescription: 'Notifications par SMS',
      pushNotificationsDescription: 'Notifications push',
      orderUpdatesDescription: 'Mises à jour de commande',
      promotionsDescription: 'Promotions',
      newsletterDescription: 'Newsletter',
      twoFactorAuthDescription: 'Authentification à deux facteurs',
      loginAlertsDescription: 'Alertes de connexion',
      personalDataDescription: 'Données personnelles',
      paymentMethodsDescription: 'Méthodes de paiement',
      deleteAccountDescription: 'Supprimer le compte',
    },
  },
  en: {
    common: {
      search: 'Search',
      cart: 'Cart',
      favorites: 'Favorites',
      login: 'Login',
      logout: 'Logout',
      profile: 'Profile',
      settings: 'Settings',
      orders: 'Orders',
      viewAll: 'View All',
      learnMore: 'Learn More',
      contactUs: 'Contact Us',
      browseProducts: 'Browse Products',
      shopNow: 'Shop Now',
      addToCart: 'Add to Cart',
      viewDetails: 'View Details',
      outOfStock: 'Out of Stock',
      lowStock: 'Low Stock',
      new: 'New',
      featured: 'Featured',
      price: 'Price',
      quantity: 'Quantity',
      total: 'Total',
      checkout: 'Checkout',
      continueShopping: 'Continue Shopping',
      remove: 'Remove',
      empty: 'Empty',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      reset: 'Reset',
      filter: 'Filter',
      sort: 'Sort',
      clear: 'Clear',
      apply: 'Apply',
      close: 'Close',
      open: 'Open',
      yes: 'Yes',
      no: 'No',
      ok: 'OK',
    },
    navigation: {
      home: 'Home',
      products: 'Products',
      favorites: 'Favorites',
      about: 'About',
      contact: 'Contact',
      categories: 'Categories',
      account: 'Account',
    },
    home: {
      heroTitle: 'Your Trusted Partner in',
      heroSubtitle: 'Quality materials, professional service, and competitive prices for all your construction needs.',
      features: {
        fastDelivery: {
          title: 'Fast Delivery',
          description: 'Same day delivery for orders placed before 2 PM',
        },
        securePayment: {
          title: 'Secure Payment',
          description: '100% secure payment with SSL encryption',
        },
        support: {
          title: '24/7 Support',
          description: 'Round the clock customer support',
        },
      },
      categories: {
        title: 'Shop by Category',
        subtitle: 'Find everything you need for your construction project',
      },
      featuredProducts: {
        title: 'Featured Products',
      },
      newProducts: {
        title: 'New Arrivals',
      },
      callToAction: {
        title: 'Ready to Start Your Project?',
        subtitle: 'Get quality construction materials delivered to your site. Contact us for bulk orders and special pricing.',
      },
    },
    products: {
      title: 'Products',
      search: 'Search products...',
      filter: 'Filter',
      sort: 'Sort',
      addToCart: 'Add to Cart',
      viewDetails: 'View Details',
      allCategories: 'All Categories',
      newest: 'Newest',
      priceAsc: 'Price: Low to High',
      priceDesc: 'Price: High to Low',
      popular: 'Most Popular',
      heroTitle: 'Discover our products',
      heroSubtitle: 'Find the best construction materials',
      shopNow: 'Shop Now',
      freeShipping: 'Free Shipping',
      securePayment: 'Secure Payment',
      support: '24/7 Support',
      easyReturns: 'Easy Returns',
      new: 'New',
      specifications: 'Specifications',
      features: 'Features',
      reviews: 'Reviews',
      stock: 'Stock',
      inStock: 'In Stock',
      lowStock: 'Low Stock',
      searchPlaceholder: 'Search for a product...',
      category: 'Category',
      sortBy: 'Sort by',
      sortName: 'Name',
      sortPriceLow: 'Price: Low to High',
      sortPriceHigh: 'Price: High to Low',
      sortRating: 'Rating',
      sortNewest: 'Newest',
      filters: 'Filters',
      priceRange: 'Price Range',
      categories: 'Categories',
      stockStatus: 'Stock Status',
      resultsFound: 'Results Found',
      noResults: 'No Results Found',
      tryDifferentSearch: 'Try a Different Search',
    },
    cart: {
      title: 'Cart',
      empty: 'Your cart is empty',
      continueShopping: 'Continue Shopping',
      checkout: 'Checkout',
      total: 'Total',
      remove: 'Remove',
      quantity: 'Quantity',
      subtotal: 'Subtotal',
      shipping: 'Shipping',
      tax: 'Tax',
      grandTotal: 'Grand Total',
      proceedToCheckout: 'Proceed to Checkout',
      updateCart: 'Update Cart',
      clearCart: 'Clear Cart',
    },
    favorites: {
      title: 'Favorites',
      empty: 'Your favorites list is empty',
      emptyDescription: 'Explore our products to add them to your favorites list',
      discoverProducts: 'Discover Products',
      clearFavorites: 'Clear Favorites',
      clearFavoritesConfirm: 'Are you sure you want to clear your favorites list?',
      favoritesCleared: 'Favorites cleared',
      inCart: 'In Cart',
      addToCart: 'Add to Cart',
      viewDetails: 'View Details',
      removeFromFavorites: 'Remove from Favorites',
      productRemoved: 'Product removed from favorites',
    },
    categories: {
      buildingMaterials: {
        name: 'Building Materials',
        description: 'Essential construction materials for any project',
      },
      toolsEquipment: {
        name: 'Tools & Equipment',
        description: 'Professional tools and construction equipment',
      },
      plumbingElectrical: {
        name: 'Plumbing & Electrical',
        description: 'Pipes, fittings, and electrical supplies',
      },
      finishingMaterials: {
        name: 'Finishing Materials',
        description: 'Materials for interior and exterior finishing',
      },
      hardwareFasteners: {
        name: 'Hardware & Fasteners',
        description: 'Screws, nails, hinges, and other hardware',
      },
    },
    footer: {
      companyDescription: 'Your trusted partner for all construction needs. Quality materials, professional service, and competitive prices.',
      quickLinks: 'Quick Links',
      categories: 'Categories',
      contactUs: 'Contact Us',
      copyright: '© {year} TotalBat. All rights reserved.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
    },
    profile: {
      title: 'Profile',
      edit: 'Edit',
      personalInfo: 'Personal Info',
      fullName: 'Full Name',
      phone: 'Phone',
      email: 'Email',
      company: 'Company',
      address: 'Address',
      registrationDate: 'Registration Date',
      recentOrders: 'Recent Orders',
      viewAllOrders: 'View All Orders',
      stats: {
        orders: 'Orders',
        favorites: 'Favorites',
        reviews: 'Reviews',
      },
      verifiedAccount: 'Verified Account',
      memberSince: 'Member Since',
      notProvided: 'Not Provided',
      editProfile: 'Edit Profile',
      firstName: 'First Name',
      lastName: 'Last Name',
      save: 'Save',
      cancel: 'Cancel',
      profileUpdated: 'Profile Updated',
      profileUpdateError: 'Profile Update Error',
    },
    settings: {
      title: 'Settings',
      notifications: 'Notifications',
      security: 'Security',
      preferences: 'Preferences',
      accountManagement: 'Account Management',
      emailNotifications: 'Email Notifications',
      smsNotifications: 'SMS Notifications',
      pushNotifications: 'Push Notifications',
      orderUpdates: 'Order Updates',
      promotions: 'Promotions',
      newsletter: 'Newsletter',
      twoFactorAuth: 'Two Factor Auth',
      loginAlerts: 'Login Alerts',
      darkMode: 'Dark Mode',
      language: 'Language',
      currency: 'Currency',
      personalData: 'Personal Data',
      paymentMethods: 'Payment Methods',
      deleteAccount: 'Delete Account',
      manage: 'Manage',
      delete: 'Delete',
      saveSettings: 'Save Settings',
      settingsSaved: 'Settings Saved',
      settingsError: 'Settings Error',
      themeUpdated: 'Theme Updated',
      languageUpdated: 'Language Updated',
      settingUpdated: 'Setting Updated',
      deleteAccountTitle: 'Delete Account',
      deleteAccountWarning: 'Are you sure you want to delete this account?',
      deleteAccountConfirm: 'Confirm Deletion',
      deleteAccountError: 'Delete Account Error',
      darkModeDescription: 'Dark Mode',
      languageDescription: 'Language',
      currencyDescription: 'Currency',
      emailNotificationsDescription: 'Email Notifications',
      smsNotificationsDescription: 'SMS Notifications',
      pushNotificationsDescription: 'Push Notifications',
      orderUpdatesDescription: 'Order Updates',
      promotionsDescription: 'Promotions',
      newsletterDescription: 'Newsletter',
      twoFactorAuthDescription: 'Two Factor Auth',
      loginAlertsDescription: 'Login Alerts',
      personalDataDescription: 'Personal Data',
      paymentMethodsDescription: 'Payment Methods',
      deleteAccountDescription: 'Delete Account',
    },
  },
};

export const getTranslation = (language: Language): Translations => {
  return translations[language] || translations.fr;
};

export default translations; 